import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type RefObject,
} from "react";

import {
  ApiRequestError,
  PolyfutureApiClient,
  type AgentChatSummary,
  type PortfolioListKind,
  type ResearchResult,
} from "./api.js";
import { AcrossPortfoliosView } from "./AcrossPortfolios.js";
import { SaveToResearch } from "./ResearchResults.js";
import { BooksCatalogView, CreateBookView, PositionSourceChooser } from "./Books.js";
import {
  AssumptionsView,
  BookSectionNav,
  PositionsView,
  RunCenterView,
  RunDetailView,
  ScenarioLibraryView,
  ScenarioVersionDiffView,
  WhatIfLabView,
} from "./BookViews.js";
import {
  hasIdentityPlatformEntry,
  loadIdentityClient,
  type IdentityClient,
  type IdentityCredential,
} from "./identity.js";
import type { AgentHoldingDraft, DraftHolding } from "./AddHoldings.js";
import {
  AgentView,
  holdingsForAgentScenarioDraft,
  loadAgentRecents,
  mergeAgentRecents,
  scenarioInputForAgentDraft,
  type AgentDraft,
  type AgentRecentChat,
} from "./Agent.js";
import { UncertainProbability } from "./Epistemics.js";
import { ProvenanceDrawer } from "./Provenance.js";
import { ConceptIcon, type ConceptIconName } from "./ConceptIcon.js";
import { Signup } from "./Signup.js";
import { formatExposureSize } from "./product-language.js";
import { Onboarding, type OnboardingResult } from "./Onboarding.js";
import {
  EmptyCatalog,
  ErrorScreen,
  LoadingScreen,
  RouteUnavailable,
  SignInBoundary,
  WorkspaceLoading,
} from "./AppStates.js";
import {
  appRoutePath,
  isBookRoute,
  resolveAppRoute,
  type ActiveView,
  type RouteIssue,
  type RouteResolution,
} from "./routes.js";
import { TodayView } from "./Today.js";
import { LaunchView } from "./Launch.js";
import { applyTheme, readStoredTheme, storeTheme, type Theme } from "./theme.js";
import { railModeFor, useElementWidth, type RailMode } from "./shellLayout.js";
import {
  OfflineBanner,
  TopbarConnection,
  useConnectionState,
  type ConnectionState,
} from "./ConnectionStatus.js";
import {
  ProductHeading,
  ProductMetric,
  ProductPage,
  ProductPanel,
  ProgressiveDisclosure,
  StatusDot,
} from "./ProductSurface.js";
import {
  isPortfolioRequiredDestination,
  parsePortfolioRequiredDestination,
  pathAfterPortfolioCreation,
  type PortfolioRequiredDestination,
} from "./portfolio-intent.js";
import type {
  BookRecord,
  CurrentUser,
  RunProgress,
  ScenarioDraftInput,
  ScenarioPolarity,
  ScenarioRecord,
  TodayProjection,
  WorkspaceData,
  AuthClientConfig,
  ReferenceProfileSummary,
} from "./types.js";

const defaultClient = new PolyfutureApiClient();
const AGENT_HOLDINGS_DRAFT_KEY = "polyfuture-agent-holdings-draft";
const AGENT_WHAT_IF_DRAFT_KEY = "polyfuture-agent-what-if-draft";
const PENDING_AGENT_SCENARIO_KEY = "polyfuture-pending-agent-scenario";
const PENDING_PORTFOLIO_DESTINATION_KEY = "polyfuture-pending-portfolio-destination";

interface PendingAgentScenario {
  readonly bookId: string;
  readonly input: ScenarioDraftInput;
}

// Keep the conversational shell and common portfolio screens eager. Operator, research, import,
// and deep-review surfaces become route chunks and do not delay the first useful interaction.
const AddHoldings = lazy(async () => ({
  default: (await import("./AddHoldings.js")).AddHoldings,
}));
const AdministrationView = lazy(async () => ({
  default: (await import("./Administration.js")).AdministrationView,
}));
const ImportDetailRouteView = lazy(async () => ({
  default: (await import("./ImportReview.js")).ImportDetailRouteView,
}));
const ImportReviewView = lazy(async () => ({
  default: (await import("./ImportReview.js")).ImportReviewView,
}));
const EvidenceView = lazy(async () => ({
  default: (await import("./Evidence.js")).EvidenceView,
}));
const CoverageView = lazy(async () => ({
  default: (await import("./Coverage.js")).CoverageView,
}));
const GovernanceView = lazy(async () => ({
  default: (await import("./Governance.js")).GovernanceView,
}));
const DecisionArtifactView = lazy(async () => ({
  default: (await import("./DecisionLoop.js")).DecisionArtifactView,
}));
const DecisionLoopView = lazy(async () => ({
  default: (await import("./DecisionLoop.js")).DecisionLoopView,
}));
const CaseLabView = lazy(async () => ({
  default: (await import("./Research.js")).CaseLabView,
}));
const ResearchCasesView = lazy(async () => ({
  default: (await import("./Research.js")).ResearchCasesView,
}));
const AccountView = lazy(async () => ({
  default: (await import("./HandoffScreens.js")).AccountView,
}));
const NewWhatIfView = lazy(async () => ({
  default: (await import("./HandoffScreens.js")).NewWhatIfView,
}));
const DataExplorerView = lazy(async () => ({
  default: (await import("./DataExplorer.js")).DataExplorerView,
}));
const MarketsView = lazy(() => import("./Markets.js"));
const CalibrationView = lazy(async () => ({
  default: (await import("./Calibration.js")).CalibrationView,
}));
const TraderView = lazy(async () => ({
  default: (await import("./trader/TraderView.js")).TraderView,
}));

type BootstrapState = "loading" | "ready" | "unauthenticated" | "entry" | "onboarding" | "error";

export function App({ client = defaultClient }: { client?: PolyfutureApiClient }) {
  const [bootstrapState, setBootstrapState] = useState<BootstrapState>("loading");
  const [authConfig, setAuthConfig] = useState<AuthClientConfig | undefined>(undefined);
  const [identity, setIdentity] = useState<IdentityClient | undefined>(undefined);
  const [credential, setCredential] = useState<IdentityCredential | undefined>(undefined);
  const [entryError, setEntryError] = useState<string | undefined>(undefined);
  const [onboardingProfiles, setOnboardingProfiles] = useState<ReferenceProfileSummary[]>([]);
  const [onboardingError, setOnboardingError] = useState<string | undefined>(undefined);
  const [onboardingBusy, setOnboardingBusy] = useState(false);
  const [profilesUnavailable, setProfilesUnavailable] = useState(false);
  const [user, setUser] = useState<CurrentUser>();
  const [books, setBooks] = useState<BookRecord[]>([]);
  const [todayProjection, setTodayProjection] = useState<TodayProjection>();
  const [selectedBookId, setSelectedBookId] = useState<string>();
  const [selectedEntityId, setSelectedEntityId] = useState<string>();
  const [selectedFromVersionId, setSelectedFromVersionId] = useState<string>();
  const [selectedToVersionId, setSelectedToVersionId] = useState<string>();
  const [activeView, setActiveView] = useState<ActiveView>("today");
  const [routeIssue, setRouteIssue] = useState<RouteIssue>();
  const [theme, setTheme] = useState<Theme>(readStoredTheme);
  const connection = useConnectionState(client);
  const [shellElement, setShellElement] = useState<HTMLDivElement | null>(null);
  const shellWidth = useElementWidth(shellElement);
  const [agentConversationOpen, setAgentConversationOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const railMode: RailMode = railModeFor(
    shellWidth,
    activeView === "agent" && agentConversationOpen,
  );
  const returnFocusToMenu = useRef(false);
  const closeDrawer = useCallback(() => {
    returnFocusToMenu.current = true;
    setDrawerOpen(false);
  }, []);

  // The menu button sits in <main>, which is inert while the drawer is open, so focus can only
  // return to it after the drawer has closed.
  useEffect(() => {
    if (drawerOpen || !returnFocusToMenu.current) return;
    returnFocusToMenu.current = false;
    menuButton.current?.focus();
  }, [drawerOpen]);

  useEffect(() => {
    if (railMode !== "drawer") setDrawerOpen(false);
  }, [railMode]);
  const [workspace, setWorkspace] = useState<WorkspaceData>();
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState<string>();
  const [quotesUpdating, setQuotesUpdating] = useState(false);
  const workspaceSnapshot = useRef<{ data: WorkspaceData; loadedAt: number } | undefined>(
    undefined,
  );
  const workspaceRequest = useRef(0);
  const currentBookId = useRef<string | undefined>(undefined);
  const [activeRunId, setActiveRunId] = useState<string>();
  const [runProgress, setRunProgress] = useState<RunProgress>();
  const [failedScenarioCount, setFailedScenarioCount] = useState(0);
  const [action, setAction] = useState<string>();
  const scenarioSubmission = useRef(false);
  const [notice, setNotice] = useState<string>();
  const [error, setError] = useState<string>();
  const [agentChatId, setAgentChatId] = useState(newAgentChatId);
  const [agentRecents, setAgentRecents] = useState<readonly AgentRecentChat[]>([]);
  // Whether the open conversation was reopened from history, so it is read from the server.
  const [agentChatFromHistory, setAgentChatFromHistory] = useState(false);
  const [agentPrefill, setAgentPrefill] = useState<{
    readonly text: string;
    readonly send: boolean;
    readonly nonce: number;
  }>();
  const serverAgentChats = useRef<readonly AgentChatSummary[]>([]);
  // "Watch with an alert rule" from a research result opens the alert form for its portfolio.
  const [alertDraftFrom, setAlertDraftFrom] = useState<{
    readonly researchResultId: string;
    readonly title: string;
    readonly bookId: string;
  }>();
  const [agentHoldingDraft, setAgentHoldingDraft] = useState<readonly AgentHoldingDraft[]>(() =>
    readSessionJson<readonly AgentHoldingDraft[]>(AGENT_HOLDINGS_DRAFT_KEY, []),
  );
  const [agentWhatIfFrame, setAgentWhatIfFrame] = useState(() =>
    readSessionJson<string>(AGENT_WHAT_IF_DRAFT_KEY, ""),
  );
  const [pendingAgentScenario, setPendingAgentScenario] = useState<
    PendingAgentScenario | undefined
  >(() => readSessionJson<PendingAgentScenario | undefined>(PENDING_AGENT_SCENARIO_KEY, undefined));
  const [pendingPortfolioDestination, setPendingPortfolioDestination] = useState<
    PortfolioRequiredDestination | undefined
  >(() =>
    parsePortfolioRequiredDestination(
      readSessionJson<unknown>(PENDING_PORTFOLIO_DESTINATION_KEY, undefined),
    ),
  );

  const applyRoute = useCallback((resolution: RouteResolution, catalog: BookRecord[]) => {
    // With no portfolio switcher, a route that names no portfolio keeps the one opened last. The
    // pages under More no longer rely on this: they list every portfolio (V-S1d).
    const lastBookId = currentBookId.current;
    const nextBookId =
      resolution.bookId ??
      (lastBookId !== undefined && catalog.some((book) => book.id === lastBookId)
        ? lastBookId
        : catalog.at(0)?.id);
    if (currentBookId.current !== nextBookId) {
      workspaceRequest.current += 1;
      setActiveRunId(undefined);
    }
    currentBookId.current = nextBookId;
    setWorkspaceLoading(false);
    setWorkspaceError(undefined);
    setActiveView(resolution.activeView);
    setRouteIssue(resolution.issue);
    setSelectedBookId(nextBookId);
    setSelectedEntityId(resolution.entityId);
    setSelectedFromVersionId(resolution.fromVersionId);
    setSelectedToVersionId(resolution.toVersionId);
    setWorkspace(
      workspaceSnapshot.current !== undefined &&
        workspaceSnapshot.current.data.book.id === nextBookId
        ? workspaceSnapshot.current.data
        : undefined,
    );
    setRunProgress(undefined);
    setFailedScenarioCount(0);
    setError(undefined);
  }, []);

  const bootstrap = useCallback(async () => {
    workspaceSnapshot.current = undefined;
    currentBookId.current = undefined;
    workspaceRequest.current += 1;
    setWorkspace(undefined);
    setBootstrapState("loading");
    setError(undefined);
    try {
      const [currentUser, projection] = await Promise.all([
        client.getCurrentUser(),
        client.getTodayProjection(),
      ]);
      const catalog = projection.books;
      const resolution = resolveAppRoute(
        new URL(window.location.href),
        catalog,
        currentUser.activeMembership.role,
      );
      setUser(currentUser);
      setBooks(catalog);
      setTodayProjection(projection);
      applyRoute(resolution, catalog);
      if (resolution.shouldReplace) {
        window.history.replaceState({}, "", resolution.canonicalPath);
      }
      setBootstrapState("ready");
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.status === 401) {
        // Authentication remains available to existing members even when self-serve organization
        // creation is disabled. The entry screen disables only the account-creation action; it
        // must not turn that policy into a sign-in outage.
        try {
          const config = await client.getAuthConfig();
          setAuthConfig(config);
          if (hasIdentityPlatformEntry(config)) {
            setIdentity(await loadIdentityClient(config.identityPlatform));
            if (window.location.pathname !== "/sign-up") {
              window.history.replaceState({}, "", "/sign-up");
            }
            setBootstrapState("entry");
            return;
          }
        } catch {
          // Fall through to the sign-in boundary; a missing config is not a signup opportunity.
        }
        setBootstrapState("unauthenticated");
        return;
      }
      setError(messageFor(caught));
      setBootstrapState("error");
    }
  }, [applyRoute, client]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  useEffect(
    () =>
      client.onUnauthorized(() => {
        workspaceSnapshot.current = undefined;
        currentBookId.current = undefined;
        workspaceRequest.current += 1;
        setWorkspace(undefined);
        setBootstrapState("unauthenticated");
        setAction(undefined);
        setWorkspaceLoading(false);
        setNotice(undefined);
        setError(undefined);
      }),
    [client],
  );

  useEffect(() => {
    if (bootstrapState !== "ready" || user === undefined) return;
    const handlePopState = () => {
      const resolution = resolveAppRoute(
        new URL(window.location.href),
        books,
        user.activeMembership.role,
      );
      applyRoute(resolution, books);
      if (resolution.shouldReplace) {
        window.history.replaceState({}, "", resolution.canonicalPath);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [applyRoute, books, bootstrapState, user]);

  useEffect(() => {
    if (user === undefined) return;
    setAgentRecents(loadAgentRecents(user.subject));
    let current = true;
    // ADR-090: the recent list comes from the server; the browser's copy fills in until it answers.
    void client.listAgentChats().then(
      (chats) => {
        if (!current) return;
        serverAgentChats.current = chats;
        setAgentRecents(mergeAgentRecents(chats, loadAgentRecents(user.subject)));
      },
      () => undefined,
    );
    return () => {
      current = false;
    };
  }, [client, user]);

  const onAgentChatsChanged = useCallback((local: readonly AgentRecentChat[]) => {
    setAgentRecents(mergeAgentRecents(serverAgentChats.current, local));
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const handleRequestError = useCallback((caught: unknown) => {
    if (caught instanceof ApiRequestError && caught.status === 401) {
      setBootstrapState("unauthenticated");
      setError(undefined);
      return;
    }
    setError(messageFor(caught));
  }, []);

  const refreshTodayProjection = useCallback(async () => {
    try {
      const projection = await client.getTodayProjection();
      setTodayProjection(projection);
      setBooks(projection.books);
    } catch (caught) {
      handleRequestError(caught);
    }
  }, [client, handleRequestError]);

  const refreshWorkspace = useCallback(
    async (force = true) => {
      if (selectedBookId === undefined) {
        setWorkspace(undefined);
        return;
      }
      if (currentBookId.current !== selectedBookId) return;
      const snapshot = workspaceSnapshot.current;
      if (
        !force &&
        snapshot?.data.book.id === selectedBookId &&
        Date.now() - snapshot.loadedAt < 30_000
      ) {
        return;
      }
      const requestId = ++workspaceRequest.current;
      const isCurrent = () =>
        workspaceRequest.current === requestId && currentBookId.current === selectedBookId;
      setWorkspaceLoading(true);
      setWorkspaceError(undefined);
      try {
        const nextWorkspace = await client.loadWorkspace(selectedBookId);
        if (!isCurrent()) return;
        workspaceSnapshot.current = { data: nextWorkspace, loadedAt: Date.now() };
        setWorkspace(nextWorkspace);
        setBooks((current) =>
          current.map((book) => (book.id === nextWorkspace.book.id ? nextWorkspace.book : book)),
        );
        const running = nextWorkspace.runs.find(
          (run) => run.status === "queued" || run.status === "running",
        );
        setActiveRunId(running?.id);
      } catch (caught) {
        if (!isCurrent()) return;
        setWorkspaceError(messageFor(caught));
        if (caught instanceof ApiRequestError && caught.status === 401) handleRequestError(caught);
      } finally {
        if (isCurrent()) setWorkspaceLoading(false);
      }
    },
    [client, handleRequestError, selectedBookId],
  );

  useEffect(() => {
    if (isBookRoute(activeView) && activeView !== "imports") void refreshWorkspace(false);
  }, [activeView, refreshWorkspace]);

  const showingPositions = activeView === "positions" || activeView === "position_detail";
  useEffect(() => {
    if (
      !showingPositions ||
      selectedBookId === undefined ||
      user?.activeMembership.role === "viewer"
    )
      return;
    let active = true;
    setQuotesUpdating(true);
    // Render holdings immediately; an external price provider must not gate navigation.
    void client
      .refreshMarketValuations(selectedBookId)
      .then(() => {
        if (active) return refreshWorkspace();
      })
      .catch((caught: unknown) => {
        if (active)
          setError(
            `Prices could not be refreshed. Showing the last saved values. ${messageFor(caught)}`,
          );
      })
      .finally(() => {
        if (active) setQuotesUpdating(false);
      });
    return () => {
      active = false;
      setQuotesUpdating(false);
    };
  }, [client, refreshWorkspace, selectedBookId, showingPositions, user?.activeMembership.role]);

  useEffect(() => {
    if (activeRunId === undefined) return;
    return client.streamScenarioRun(activeRunId, {
      onProgress: setRunProgress,
      onScenarioCompleted: (scenario) => {
        const snapshot = workspaceSnapshot.current;
        if (snapshot !== undefined && snapshot.data.book.id === currentBookId.current) {
          workspaceSnapshot.current = {
            ...snapshot,
            data: {
              ...snapshot.data,
              scenarios: upsertScenario(snapshot.data.scenarios, scenario),
            },
          };
        }
        setWorkspace((current) =>
          current === undefined
            ? current
            : {
                ...current,
                scenarios: upsertScenario(current.scenarios, scenario),
              },
        );
      },
      onScenarioFailed: () => setFailedScenarioCount((current) => current + 1),
      onCompleted: () => {
        setNotice("What-if run completed. The portfolio is current.");
        setActiveRunId(undefined);
        setRunProgress(undefined);
        void refreshWorkspace();
      },
    });
  }, [activeRunId, client, refreshWorkspace]);

  const hasActiveQuickTest =
    activeView === "whatif" &&
    workspace?.whatIfs.some((run) => run.status === "queued" || run.status === "running") === true;

  useEffect(() => {
    if (!hasActiveQuickTest) return;
    let refreshing = false;
    const timer = window.setInterval(() => {
      if (refreshing) return;
      refreshing = true;
      void refreshWorkspace().finally(() => {
        refreshing = false;
      });
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [hasActiveQuickTest, refreshWorkspace]);

  const navigate = useCallback(
    (
      view: ActiveView,
      bookId = selectedBookId,
      entityId?: string,
      fromVersionId?: string,
      toVersionId?: string,
    ) => {
      if (user === undefined) return;
      const path = appRoutePath(view, bookId, entityId, fromVersionId, toVersionId);
      window.history.pushState({}, "", path);
      applyRoute(
        resolveAppRoute(new URL(window.location.href), books, user.activeMembership.role),
        books,
      );
    },
    [applyRoute, books, selectedBookId, user],
  );

  const selectBook = (bookId: string) => navigate("workspace", bookId);
  const selectView = (view: ActiveView) => navigate(view);

  const clearPendingPortfolioDestination = () => {
    setPendingPortfolioDestination(undefined);
    removeSessionItem(PENDING_PORTFOLIO_DESTINATION_KEY);
  };

  const startPortfolioRequiredFlow = (destination: PortfolioRequiredDestination) => {
    if (selectedBookId !== undefined) {
      navigate(destination, selectedBookId);
      return;
    }
    setPendingPortfolioDestination(destination);
    writeSessionJson(PENDING_PORTFOLIO_DESTINATION_KEY, destination);
    if (user?.activeMembership.role !== "admin") {
      setNotice(
        "An organization administrator must create the first portfolio. Your draft remains saved.",
      );
      navigate("books");
      return;
    }
    setNotice("Create the first portfolio, then Polyfuture will continue where you started.");
    navigate("create_book");
  };

  const startDirectPortfolioCreation = () => {
    clearPendingPortfolioDestination();
    navigate("create_book");
  };

  const openAgentDraft = (draft: AgentDraft) => {
    if (draft.destination === "add_holdings") {
      const holdings = draft.holdings ?? [];
      setAgentHoldingDraft(holdings);
      writeSessionJson(AGENT_HOLDINGS_DRAFT_KEY, holdings);
    }
    if (draft.destination === "new_scenario") {
      const scenarioInput = scenarioInputForAgentDraft(draft);
      if (scenarioInput !== undefined && selectedBookId !== undefined) {
        const selectedBook = books.find((book) => book.id === selectedBookId);
        if (
          selectedBook === undefined ||
          selectedBook.currentBookVersionId === undefined ||
          selectedBook.positionCount === 0
        ) {
          const recognizedHoldings = holdingsForAgentScenarioDraft(draft);
          if (recognizedHoldings.length > 0) {
            setAgentHoldingDraft(recognizedHoldings);
            writeSessionJson(AGENT_HOLDINGS_DRAFT_KEY, recognizedHoldings);
          }
          const pending = { bookId: selectedBookId, input: scenarioInput };
          setPendingAgentScenario(pending);
          writeSessionJson(PENDING_AGENT_SCENARIO_KEY, pending);
          setNotice(
            recognizedHoldings.length > 0
              ? "Review and publish the holdings below. Polyfuture will then create and run your saved what-if automatically."
              : "Add and publish at least one holding. Polyfuture saved your what-if and will run it after the portfolio has a versioned input.",
          );
          navigate("add_holdings", selectedBookId);
          return;
        }
        void createGlobalScenario(selectedBookId, scenarioInput);
        return;
      }
      const frame = draft.statedOrigin ?? "";
      setAgentWhatIfFrame(frame);
      writeSessionJson(AGENT_WHAT_IF_DRAFT_KEY, frame);
    }
    if (draft.destination === "create_book") {
      startDirectPortfolioCreation();
      return;
    }
    if (selectedBookId === undefined && isPortfolioRequiredDestination(draft.destination)) {
      startPortfolioRequiredFlow(draft.destination);
      return;
    }
    navigate(draft.destination);
  };

  const startNewAgentChat = () => {
    setAgentChatId(newAgentChatId());
    setAgentChatFromHistory(false);
    navigate("agent");
  };

  // V-M1 and V-M5: Market research hands a question to a new Ask conversation. "Ask about" fills
  // the composer and waits; a question typed into search is sent.
  const askFromMarkets = (text: string, send: boolean) => {
    setAgentPrefill({ text, send, nonce: Date.now() });
    startNewAgentChat();
  };

  const watchResearchResult = (result: ResearchResult) => {
    setAlertDraftFrom({ researchResultId: result.id, title: result.title, bookId: result.bookId });
    navigate("evidence_new", result.bookId);
  };

  const openAgentChat = (chatId: string) => {
    setAgentChatId(chatId);
    setAgentChatFromHistory(true);
    navigate("agent");
  };

  const finishBookCreation = async (book: BookRecord) => {
    if (user === undefined) return;
    try {
      const projection = await client.getTodayProjection();
      setTodayProjection(projection);
      setBooks(projection.books);
      setNotice(
        `${book.name} was created as a draft. Publish inputs before treating it as monitored.`,
      );
      const destination = pendingPortfolioDestination;
      clearPendingPortfolioDestination();
      const path = pathAfterPortfolioCreation(book.id, destination);
      window.history.pushState({}, "", path);
      applyRoute(
        resolveAppRoute(
          new URL(window.location.href),
          projection.books,
          user.activeMembership.role,
        ),
        projection.books,
      );
    } catch (caught) {
      handleRequestError(caught);
    }
  };

  const performAction = async (label: string, operation: () => Promise<unknown>) => {
    setAction(label);
    setNotice(undefined);
    setError(undefined);
    try {
      await operation();
      workspaceSnapshot.current = undefined;
      await Promise.all([refreshWorkspace(), refreshTodayProjection()]);
      return true;
    } catch (caught) {
      handleRequestError(caught);
      return false;
    } finally {
      setAction(undefined);
    }
  };

  const launchRun = async () => {
    if (selectedBookId === undefined) return;
    setAction("run");
    setNotice(undefined);
    setError(undefined);
    try {
      const run = await client.createScenarioRun(selectedBookId);
      workspaceSnapshot.current = undefined;
      setActiveRunId(run.id);
      setRunProgress({ runId: run.id, phase: "elaborating", done: 0, total: 0 });
      setNotice("Targeted run accepted. Progress is streaming from the durable event log.");
      await refreshWorkspace();
    } catch (caught) {
      handleRequestError(caught);
    } finally {
      setAction(undefined);
    }
  };

  const rejectRestrictedIdentity = async () => {
    try {
      await identity?.deleteCurrentUser();
    } catch {
      // The API has already denied the session and organization boundary. A second sign-out
      // attempt clears local provider state even if Firebase could not remove the account.
      await identity?.signOut().catch(() => undefined);
    }
    setCredential(undefined);
    setOnboardingError(undefined);
    setEntryError("Access is allowed only for approved accounts.");
    window.history.replaceState({}, "", "/sign-up");
    setBootstrapState("entry");
  };

  const handleAuthenticated = async (issued: IdentityCredential) => {
    setCredential(issued);
    setEntryError(undefined);
    setOnboardingError(undefined);
    try {
      // An allowed identity without a membership may begin onboarding. A private-beta rejection
      // is deliberately a different machine outcome: it must never be interpreted as permission
      // to create an organization.
      await client.exchangeSession(issued.idToken);
      await bootstrap();
    } catch (caught) {
      if (
        caught instanceof ApiRequestError &&
        caught.status === 403 &&
        caught.code === "membership_required"
      ) {
        await loadOnboardingProfiles();
        window.history.replaceState({}, "", "/onboarding");
        setBootstrapState("onboarding");
        return;
      }
      if (
        caught instanceof ApiRequestError &&
        caught.status === 403 &&
        caught.code === "access_restricted"
      ) {
        await rejectRestrictedIdentity();
        return;
      }
      setEntryError(messageFor(caught));
    }
  };

  const loadOnboardingProfiles = async () => {
    setProfilesUnavailable(false);
    try {
      setOnboardingProfiles(await client.listOnboardingProfiles());
    } catch {
      // Distinguish "could not load" from "none published"; the first is retryable and the
      // difference decides whether the operator is looking at a fault or at a real empty catalog.
      setOnboardingProfiles([]);
      setProfilesUnavailable(true);
    }
  };

  const publishHoldings = async (book: BookRecord, holdings: DraftHolding[]) => {
    if (holdings.length === 0) return;
    setAction("publish-holdings");
    setError(undefined);
    try {
      // The reviewed draft is one governed change: validation or persistence failure rolls the
      // entire set back, and success creates exactly one immutable book version.
      const publication = await client.createPositions(
        book.id,
        holdings.map((holding) => ({
          values: holdingValuesForProfile(book.profileKey, holding),
        })),
      );
      workspaceSnapshot.current = undefined;
      const coverageRun = publication.initialCoverage?.coverageRun;
      if (coverageRun !== undefined) {
        setActiveRunId(coverageRun.id);
        setRunProgress({ runId: coverageRun.id, phase: "selecting", done: 0, total: 0 });
      }
      setAgentHoldingDraft([]);
      removeSessionItem(AGENT_HOLDINGS_DRAFT_KEY);
      const pendingScenario =
        pendingAgentScenario?.bookId === book.id ? pendingAgentScenario : undefined;
      if (pendingScenario !== undefined) {
        try {
          const scenario = await client.createScenario(book.id, pendingScenario.input);
          setPendingAgentScenario(undefined);
          removeSessionItem(PENDING_AGENT_SCENARIO_KEY);
          setAgentWhatIfFrame("");
          removeSessionItem(AGENT_WHAT_IF_DRAFT_KEY);
          setNotice(
            `Published ${holdings.length} holding(s), created the saved what-if, and queued its analysis. Monitoring remains off until the portfolio goes live.`,
          );
          navigate("scenario_detail", book.id, scenario.id);
          void refreshTodayProjection();
          return;
        } catch (caught) {
          setAgentWhatIfFrame(pendingScenario.input.thesis);
          writeSessionJson(AGENT_WHAT_IF_DRAFT_KEY, pendingScenario.input.thesis);
          setNotice(
            `Published ${holdings.length} holding(s). The what-if is still saved locally and can be retried without entering the holdings again.`,
          );
          navigate("new_scenario", book.id);
          await refreshWorkspace();
          handleRequestError(caught);
          return;
        }
      }
      setNotice(
        coverageRun === undefined
          ? `Published ${holdings.length} holding(s). Monitoring does not start until the portfolio goes live.`
          : `Published ${holdings.length} holding(s) and queued initial what-if coverage. Monitoring remains off until the portfolio goes live.`,
      );
      await refreshWorkspace();
      navigate("positions", book.id);
    } catch (caught) {
      handleRequestError(caught);
    } finally {
      setAction(undefined);
    }
  };

  // V-A3: a what-if draft saved from Ask stays in the conversation; the caller shows "Saved" only
  // after this resolves with the server's id.
  const saveAgentWhatIf = async (bookId: string, input: ScenarioDraftInput): Promise<string> => {
    const scenario = await client.createScenario(bookId, input);
    workspaceSnapshot.current = undefined;
    void refreshTodayProjection();
    return scenario.id;
  };

  const createGlobalScenario = async (bookId: string, input: ScenarioDraftInput) => {
    if (scenarioSubmission.current) return;
    scenarioSubmission.current = true;
    setAction("scenario");
    setError(undefined);
    try {
      const scenario = await client.createScenario(bookId, input);
      if (pendingAgentScenario?.bookId === bookId) {
        setPendingAgentScenario(undefined);
        removeSessionItem(PENDING_AGENT_SCENARIO_KEY);
      }
      setAgentWhatIfFrame("");
      removeSessionItem(AGENT_WHAT_IF_DRAFT_KEY);
      setNotice(
        input.startElaboration === false
          ? "Draft saved. No model run was started and monitoring remains off."
          : "The proposed what-if was saved and its targeted elaboration was queued.",
      );
      workspaceSnapshot.current = undefined;
      navigate("scenario_detail", bookId, scenario.id);
      void refreshTodayProjection();
    } catch (caught) {
      handleRequestError(caught);
    } finally {
      scenarioSubmission.current = false;
      setAction(undefined);
    }
  };

  const promoteQuickTest = async (
    runId: string,
    input: { name: string; polarity: ScenarioPolarity; falsification: string },
  ): Promise<boolean> => {
    setAction("promote-whatif");
    setNotice(undefined);
    setError(undefined);
    try {
      const promoted = await client.promoteWhatIf(runId, input);
      workspaceSnapshot.current = undefined;
      await Promise.all([refreshWorkspace(), refreshTodayProjection()]);
      setNotice(
        "Governed draft created. The quick test is unchanged and no elaboration run was started.",
      );
      navigate("scenario_detail", promoted.bookId, promoted.scenarioId);
      return true;
    } catch (caught) {
      handleRequestError(caught);
      return false;
    } finally {
      setAction(undefined);
    }
  };

  const handleOnboardingComplete = async (result: OnboardingResult) => {
    if (credential === undefined) return;
    setOnboardingBusy(true);
    setOnboardingError(undefined);
    try {
      const organization = await client.createOrganization({
        idToken: credential.idToken,
        organizationName: result.organizationName,
        posture: result.posture,
        startingPath: result.startingPath,
        ...(result.profileKey.length === 0 ? {} : { profileKey: result.profileKey }),
        ...(result.profileVersion.length === 0 ? {} : { profileVersion: result.profileVersion }),
      });
      await client.exchangeSession(credential.idToken, organization.orgId);
      window.history.replaceState({}, "", "/get-started");
      await bootstrap();
    } catch (caught) {
      if (
        caught instanceof ApiRequestError &&
        caught.status === 403 &&
        caught.code === "access_restricted"
      ) {
        await rejectRestrictedIdentity();
        return;
      }
      setOnboardingError(messageFor(caught));
    } finally {
      setOnboardingBusy(false);
    }
  };

  if (bootstrapState === "loading") return <LoadingScreen />;
  if (bootstrapState === "unauthenticated") return <SignInBoundary onRetry={bootstrap} />;
  if (bootstrapState === "entry" && authConfig !== undefined) {
    return (
      <Signup
        config={authConfig}
        identity={identity}
        authenticationError={entryError}
        onAuthenticated={(issued) => void handleAuthenticated(issued)}
      />
    );
  }
  if (bootstrapState === "onboarding") {
    return (
      <Onboarding
        profiles={onboardingProfiles}
        suggestedName={suggestedOrganizationName(credential)}
        submitting={onboardingBusy}
        error={onboardingError}
        profilesUnavailable={profilesUnavailable}
        onRetryProfiles={() => void loadOnboardingProfiles()}
        onComplete={(result) => void handleOnboardingComplete(result)}
        onSkip={(result) => void handleOnboardingComplete(result)}
      />
    );
  }
  if (bootstrapState === "error" || user === undefined) {
    return (
      <ErrorScreen message={error ?? "The workspace could not be opened."} onRetry={bootstrap} />
    );
  }

  const selectedBook = books.find((book) => book.id === selectedBookId);
  return (
    <div
      ref={setShellElement}
      className="app-shell"
      data-theme={theme}
      data-rail={railMode}
      data-drawer={railMode === "drawer" && drawerOpen ? "open" : undefined}
    >
      <a className="skip-link" href="#workspace-main">
        Skip to workspace
      </a>
      <BookRail
        books={books}
        selectedBookId={selectedBookId}
        user={user}
        activeView={activeView}
        agentRecents={agentRecents}
        attentionCount={
          todayProjection === undefined
            ? undefined
            : todayProjection.summary.needsActionCount + todayProjection.summary.needsReviewCount
        }
        mode={railMode}
        drawerOpen={drawerOpen}
        onCloseDrawer={closeDrawer}
        onSelectView={(view) => {
          closeDrawer();
          selectView(view);
        }}
        onNewAgentChat={() => {
          closeDrawer();
          startNewAgentChat();
        }}
        onOpenAgentChat={(chatId) => {
          closeDrawer();
          openAgentChat(chatId);
        }}
        onOpenAccount={() => {
          closeDrawer();
          navigate("account");
        }}
        onSwitchOrganization={(orgId) =>
          performAction("organization", async () => {
            await client.switchOrganization(orgId);
            window.location.assign("/dashboard");
          })
        }
        onLogout={() =>
          performAction("logout", async () => {
            await client.logout();
            window.location.assign("/dashboard");
          })
        }
      />
      {railMode === "drawer" && drawerOpen ? (
        <div className="rail-scrim" aria-hidden="true" onClick={closeDrawer} />
      ) : null}
      <main
        id="workspace-main"
        className="workspace-main"
        inert={railMode === "drawer" && drawerOpen}
      >
        <OfflineBanner state={connection} />
        <ProductTopbar
          activeView={activeView}
          selectedBook={selectedBook}
          localDemoAccount={user.subject.includes("local-demo")}
          theme={theme}
          connection={connection}
          {...(railMode === "drawer"
            ? {
                menu: {
                  ref: menuButton,
                  expanded: drawerOpen,
                  onOpen: () => setDrawerOpen(true),
                },
              }
            : {})}
          onToggleTheme={() => {
            const next: Theme = theme === "dark" ? "light" : "dark";
            storeTheme(next);
            setTheme(next);
          }}
        />
        <div className="workspace-content">
          {error === undefined ? null : (
            <div className="banner banner-error" role="alert">
              <span>{error}</span>
              <button type="button" onClick={() => setError(undefined)} aria-label="Dismiss error">
                Dismiss
              </button>
            </div>
          )}
          {notice === undefined ? null : (
            <div className="banner banner-notice" role="status">
              {notice}
            </div>
          )}
          <Suspense fallback={<WorkspaceLoading />}>
            {routeIssue !== undefined ? (
              <RouteUnavailable issue={routeIssue} onReturnToday={() => navigate("today")} />
            ) : activeView === "launch" ? (
              <LaunchView
                profileName={books.at(0)?.profileDisplayName ?? "Your profile"}
                hasPortfolio={books.length > 0}
                onAddHoldings={() => startPortfolioRequiredFlow("add_holdings")}
                onCreatePortfolio={startDirectPortfolioCreation}
                onStateWhatIf={() => startPortfolioRequiredFlow("new_scenario")}
                onReplayCase={() => navigate("case_lab")}
                onWatchNumber={() => startPortfolioRequiredFlow("evidence")}
                onOpenDashboard={() => navigate("today")}
              />
            ) : activeView === "agent" ? (
              <AgentView
                client={client}
                storageScope={user.subject}
                chatId={agentChatId}
                fromHistory={agentChatFromHistory}
                {...(selectedBookId === undefined ? {} : { selectedBookId })}
                onChatsChanged={onAgentChatsChanged}
                onConversationOpenChange={setAgentConversationOpen}
                {...(agentPrefill === undefined ? {} : { prefill: agentPrefill })}
                onPrefillUsed={() => setAgentPrefill(undefined)}
                onOpenDraft={openAgentDraft}
                openingDraft={action !== undefined}
                books={books}
                canWrite={user.activeMembership.role !== "viewer"}
                onSaveWhatIf={saveAgentWhatIf}
                onOpenWhatIf={(bookId, scenarioId) =>
                  navigate("scenario_detail", bookId, scenarioId)
                }
                onOpenGoal={(goalId) => navigate("trading_goal", undefined, goalId)}
                renderAnswerActions={(answer, question) => (
                  <SaveToResearch
                    client={client}
                    role={user.activeMembership.role}
                    answer={answer}
                    question={question}
                    portfolios={books}
                    defaultPortfolioId={selectedBookId}
                    onWatch={watchResearchResult}
                  />
                )}
              />
            ) : activeView === "calibration" ? (
              <CalibrationView client={client} />
            ) : acrossPortfolioKind(activeView) !== undefined ? (
              <AcrossPortfoliosView
                client={client}
                kind={acrossPortfolioKind(activeView)!}
                portfolios={books}
                onOpenRow={(row) =>
                  row.target === "investigation"
                    ? navigate("investigation_detail", row.bookId, row.id)
                    : row.target === "report"
                      ? navigate("report_detail", undefined, row.id)
                      : row.target === "analysis_run"
                        ? navigate("run_detail", undefined, row.id)
                        : navigate("governance", row.bookId)
                }
                onOpenPortfolio={(bookId) =>
                  navigate(PORTFOLIO_PAGE_FOR[acrossPortfolioKind(activeView)!], bookId)
                }
              />
            ) : activeView === "trading" || activeView === "trading_goal" ? (
              <TraderView
                client={client}
                role={user.activeMembership.role}
                books={books}
                goalId={activeView === "trading_goal" ? selectedEntityId : undefined}
                onOpenGoal={(goalId) => navigate("trading_goal", undefined, goalId)}
                onOpenList={() => navigate("trading")}
              />
            ) : activeView === "account" ? (
              <AccountView user={user} />
            ) : activeView === "admin" ? (
              <AdministrationView
                client={client}
                books={books}
                currentUser={user}
                onBack={() => selectView("workspace")}
              />
            ) : activeView === "research" ? (
              <ResearchCasesView client={client} onOpenLab={() => navigate("case_lab")} />
            ) : activeView === "case_lab" ? (
              <CaseLabView client={client} onBack={() => navigate("research")} />
            ) : activeView === "books" ? (
              todayProjection === undefined ? (
                <WorkspaceLoading />
              ) : (
                <BooksCatalogView
                  projection={todayProjection}
                  onOpenBook={selectBook}
                  onCreateBook={startDirectPortfolioCreation}
                />
              )
            ) : activeView === "create_book" ? (
              <CreateBookView
                client={client}
                onCancel={() => {
                  const returnToLauncher = pendingPortfolioDestination !== undefined;
                  clearPendingPortfolioDestination();
                  navigate(returnToLauncher ? "launch" : "books");
                }}
                onCreated={(book) => void finishBookCreation(book)}
              />
            ) : activeView === "today" ? (
              todayProjection === undefined ? (
                <WorkspaceLoading />
              ) : (
                <TodayView
                  projection={todayProjection}
                  client={client}
                  bookId={selectedBookId}
                  onOpenBook={selectBook}
                  onOpenChange={(change) => {
                    const { view, bookId } = change.destination;
                    if (view === "whatifs") navigate("whatif", bookId);
                    else if (view === "scenarios") navigate("scenarios", bookId);
                    else if (view === "evidence") navigate("evidence", bookId);
                    else navigate("workspace", bookId);
                  }}
                  onOpenAttentionItem={(item) => {
                    const { view, entityId } = item.destination;
                    if (view === "episode" && entityId !== undefined)
                      navigate("episode_detail", item.bookId, entityId);
                    else if (view === "assumption" && entityId !== undefined)
                      navigate("assumption_detail", item.bookId, entityId);
                    else if (view === "investigation" && entityId !== undefined)
                      navigate("investigation_detail", item.bookId, entityId);
                    else if (view === "run" && entityId !== undefined)
                      navigate("run_detail", undefined, entityId);
                    else if (view === "evidence") navigate("evidence", item.bookId);
                    else navigate("workspace", item.bookId);
                  }}
                  canCreateBook={user.activeMembership.role === "admin"}
                  onCreateBook={startDirectPortfolioCreation}
                  onOpenWhatIfs={() => navigate("scenarios")}
                />
              )
            ) : activeView === "scenarios" || activeView === "scenario_detail" ? (
              <ScenarioLibraryView
                client={client}
                books={books}
                selectedId={activeView === "scenario_detail" ? selectedEntityId : undefined}
                role={user.activeMembership.role}
                onOpen={(id) => navigate("scenario_detail", undefined, id)}
                onBack={() => navigate("scenarios")}
                onOpenPosition={(bookId, id) => navigate("position_detail", bookId, id)}
                onCreate={() => navigate("new_scenario")}
                onOpenCoverage={() => navigate("coverage")}
                onOpenDiff={(scenarioId, fromVersionId, toVersionId) =>
                  navigate("scenario_diff", undefined, scenarioId, fromVersionId, toVersionId)
                }
              />
            ) : activeView === "scenario_diff" &&
              selectedEntityId !== undefined &&
              selectedFromVersionId !== undefined &&
              selectedToVersionId !== undefined ? (
              <ScenarioVersionDiffView
                scenarioId={selectedEntityId}
                fromVersionId={selectedFromVersionId}
                toVersionId={selectedToVersionId}
                client={client}
                onBack={() => navigate("scenario_detail", undefined, selectedEntityId)}
              />
            ) : activeView === "coverage" ? (
              <CoverageView
                books={books}
                client={client}
                onBack={() => navigate("scenarios")}
                onOpenPosition={(bookId, positionId) =>
                  navigate("position_detail", bookId, positionId)
                }
                onOpenEvidence={(bookId) => navigate("evidence", bookId)}
              />
            ) : activeView === "new_scenario" ? (
              <NewWhatIfView
                initialFrame={agentWhatIfFrame}
                portfolios={books}
                client={client}
                {...(selectedBookId === undefined ? {} : { initialPortfolioId: selectedBookId })}
                canCreateWhatIf={user.activeMembership.role !== "viewer"}
                canCreatePortfolio={user.activeMembership.role === "admin"}
                busy={action !== undefined}
                onCancel={() => navigate("scenarios")}
                onCreate={(bookId, input) => void createGlobalScenario(bookId, input)}
                onCreatePortfolio={() => startPortfolioRequiredFlow("new_scenario")}
              />
            ) : activeView === "run_detail" && selectedEntityId !== undefined ? (
              <RunDetailView
                runId={selectedEntityId}
                client={client}
                onBack={(bookId) => navigate("runs", bookId)}
                onOpenScenario={(scenarioId) => navigate("scenario_detail", undefined, scenarioId)}
              />
            ) : activeView === "report_detail" && selectedEntityId !== undefined ? (
              <DecisionArtifactView
                artifactId={selectedEntityId}
                user={user}
                client={client}
                onBack={(bookId) => navigate("decision_packs", bookId)}
              />
            ) : activeView === "data" ? (
              <MarketsView
                client={client}
                books={books}
                {...(selectedBookId === undefined ? {} : { selectedBookId })}
                canWrite={user.activeMembership.role !== "viewer"}
                onAsk={askFromMarkets}
                onOpenWhatIf={(bookId, scenarioId) =>
                  navigate("scenario_detail", bookId, scenarioId)
                }
                episodes={
                  <AcrossPortfoliosView
                    embedded
                    client={client}
                    kind="episodes"
                    portfolios={books}
                    onOpenRow={(row) => navigate("episode_detail", row.bookId, row.id)}
                    onOpenPortfolio={(bookId) => navigate("evidence", bookId)}
                  />
                }
              />
            ) : activeView === "advanced_data" || activeView === "saved_results" ? (
              <DataExplorerView
                key={activeView}
                client={client}
                role={user.activeMembership.role}
                onOpenAdministration={() => navigate("admin")}
                onWatchResearch={watchResearchResult}
                initialTab={activeView === "saved_results" ? "results" : "packs"}
              />
            ) : activeView === "import_detail" && selectedEntityId !== undefined ? (
              <ImportDetailRouteView
                importId={selectedEntityId}
                role={user.activeMembership.role}
                client={client}
                onBack={(bookId) => navigate("imports", bookId)}
                onOpenImport={(importId) => navigate("import_detail", undefined, importId)}
                onAccepted={(bookId) => {
                  setNotice(
                    "The accepted snapshot is now the current immutable portfolio version.",
                  );
                  workspaceSnapshot.current = undefined;
                  setWorkspace(undefined);
                  navigate("workspace", bookId);
                  void refreshTodayProjection();
                }}
              />
            ) : selectedBookId === undefined ? (
              <EmptyCatalog />
            ) : activeView === "connect" ? (
              <PositionSourceChooser
                onChooseFile={() => navigate("imports", selectedBookId)}
                onChooseManual={() => navigate("positions", selectedBookId)}
                onChooseSftp={() => navigate("admin")}
              />
            ) : activeView === "imports" ? (
              <ImportReviewView
                bookId={selectedBookId}
                role={user.activeMembership.role}
                client={client}
                onBack={() => selectView("workspace")}
                onOpenImport={(importId) => navigate("import_detail", undefined, importId)}
                onAccepted={(bookId) => {
                  setNotice(
                    "The accepted snapshot is now the current immutable portfolio version.",
                  );
                  workspaceSnapshot.current = undefined;
                  setWorkspace(undefined);
                  navigate("workspace", bookId);
                  void refreshTodayProjection();
                }}
              />
            ) : workspace === undefined ? (
              workspaceError === undefined ? (
                <WorkspaceLoading />
              ) : (
                <section role="alert">
                  <h2>We couldn’t open this portfolio</h2>
                  <p>{workspaceError}</p>
                  <button
                    className="button button-primary"
                    disabled={workspaceLoading}
                    onClick={() => void refreshWorkspace()}
                  >
                    {workspaceLoading ? "Retrying…" : "Try again"}
                  </button>
                  <button className="button" onClick={() => navigate("books")}>
                    Back to portfolios
                  </button>
                </section>
              )
            ) : (
              <>
                {workspaceLoading || quotesUpdating ? (
                  <p role="status">
                    {quotesUpdating
                      ? "Updating prices… Your saved holdings are ready to use."
                      : "Updating portfolio…"}
                  </p>
                ) : null}
                {workspaceError === undefined ? null : (
                  <p role="alert">
                    Couldn’t update this portfolio. Showing saved data.{" "}
                    <button className="button" onClick={() => void refreshWorkspace()}>
                      Retry
                    </button>
                  </p>
                )}
                {showsPortfolioSectionNav(activeView) ? (
                  <BookSectionNav
                    activeView={activeView}
                    onNavigate={(view) => navigate(view, workspace.book.id)}
                  />
                ) : null}
                {activeView === "add_holdings" ? (
                  <AddHoldings
                    client={client}
                    portfolioName={workspace.book.name}
                    existing={workspace.positions}
                    profileKey={workspace.book.profileKey}
                    profileName={workspace.book.profileDisplayName}
                    unpricedHoldingsPermitted={workspace.book.unpricedHoldingsPermitted === true}
                    canPublish={user.activeMembership.role !== "viewer"}
                    publishing={action === "publish-holdings"}
                    error={error}
                    onPublish={(holdings) => void publishHoldings(workspace.book, holdings)}
                    onOpenFileIntake={() => navigate("imports", workspace.book.id)}
                    agentDraft={agentHoldingDraft}
                    onAgentDraftChange={(draft) => {
                      setAgentHoldingDraft(draft);
                      writeSessionJson(AGENT_HOLDINGS_DRAFT_KEY, draft);
                    }}
                  />
                ) : activeView === "positions" || activeView === "position_detail" ? (
                  <PositionsView
                    data={workspace}
                    selectedId={activeView === "position_detail" ? selectedEntityId : undefined}
                    onOpen={(id) => navigate("position_detail", workspace.book.id, id)}
                    onBack={() => navigate("positions", workspace.book.id)}
                  />
                ) : activeView === "assumptions" || activeView === "assumption_detail" ? (
                  <AssumptionsView
                    data={workspace}
                    selectedId={activeView === "assumption_detail" ? selectedEntityId : undefined}
                    role={user.activeMembership.role}
                    busy={action !== undefined}
                    onOpen={(id) => navigate("assumption_detail", workspace.book.id, id)}
                    onBack={() => navigate("assumptions", workspace.book.id)}
                    onUpdate={async (id, input) => {
                      const saved = await performAction("assumption-revision", () =>
                        client.updateAssumption(id, input),
                      );
                      if (saved) {
                        setNotice(
                          "Assumption revision published as a new immutable portfolio version.",
                        );
                      }
                      return saved;
                    }}
                    onRetire={async (id, revision, rationale) => {
                      const retired = await performAction("assumption-retirement", () =>
                        client.retireAssumption(id, { expectedRevision: revision, rationale }),
                      );
                      if (retired) {
                        setNotice(
                          "Assumption retired. Directly linked what-ifs are queued for reassessment review.",
                        );
                        navigate("assumptions", workspace.book.id);
                      }
                      return retired;
                    }}
                  />
                ) : activeView === "evidence" ||
                  activeView === "evidence_new" ||
                  activeView === "evidence_detail" ||
                  activeView === "episode_detail" ? (
                  <EvidenceView
                    bookId={workspace.book.id}
                    selectedId={activeView === "evidence_detail" ? selectedEntityId : undefined}
                    episodeId={activeView === "episode_detail" ? selectedEntityId : undefined}
                    user={user}
                    client={client}
                    onOpen={(id) => navigate("evidence_detail", workspace.book.id, id)}
                    onOpenEpisode={(id) => navigate("episode_detail", workspace.book.id, id)}
                    onOpenScenario={(id) => navigate("scenario_detail", workspace.book.id, id)}
                    onOpenPosition={(id) => navigate("position_detail", workspace.book.id, id)}
                    onInvestigate={() => navigate("investigations", workspace.book.id)}
                    mode={activeView === "evidence_new" ? "create" : "catalog"}
                    onOpenCreate={() => navigate("evidence_new", workspace.book.id)}
                    onCreated={() => {
                      setNotice("Evidence contract approved. It remains inactive until observed.");
                      navigate("evidence", workspace.book.id);
                    }}
                    onBack={() => navigate("evidence", workspace.book.id)}
                    draftFrom={
                      activeView === "evidence_new" && alertDraftFrom?.bookId === workspace.book.id
                        ? alertDraftFrom
                        : undefined
                    }
                    onDraftSaved={() => {
                      setAlertDraftFrom(undefined);
                      setNotice(
                        "Alert rule saved as a draft. It watches nothing until a source is connected and it is approved.",
                      );
                      navigate("evidence", workspace.book.id);
                    }}
                  />
                ) : activeView === "governance" || activeView === "thesis" ? (
                  <GovernanceView
                    bookId={workspace.book.id}
                    mode={activeView === "thesis" ? "extraction" : "dashboard"}
                    user={user}
                    client={client}
                    onModeChange={(mode) =>
                      navigate(mode === "extraction" ? "thesis" : "governance", workspace.book.id)
                    }
                    onChanged={refreshWorkspace}
                  />
                ) : activeView === "investigations" ||
                  activeView === "investigation_detail" ||
                  activeView === "decision_packs" ? (
                  <DecisionLoopView
                    bookId={workspace.book.id}
                    {...(activeView === "investigation_detail" && selectedEntityId !== undefined
                      ? { selectedId: selectedEntityId }
                      : {})}
                    mode={activeView === "decision_packs" ? "packs" : "investigations"}
                    user={user}
                    workspace={workspace}
                    client={client}
                    onOpen={(id) => navigate("investigation_detail", workspace.book.id, id)}
                    onOpenArtifact={(id) => navigate("report_detail", undefined, id)}
                    onBack={() => navigate("investigations", workspace.book.id)}
                  />
                ) : activeView === "runs" ? (
                  <RunCenterView
                    data={workspace}
                    client={client}
                    role={user.activeMembership.role}
                    busy={action !== undefined}
                    progress={runProgress}
                    onLaunch={() => void launchRun()}
                    onOpen={(runId) => navigate("run_detail", undefined, runId)}
                  />
                ) : activeView === "whatif" ? (
                  <WhatIfLabView
                    data={workspace}
                    role={user.activeMembership.role}
                    busy={action !== undefined}
                    onCreate={(shock) =>
                      void performAction("whatif", () =>
                        client.createWhatIf(workspace.book.id, shock),
                      )
                    }
                    onPromote={promoteQuickTest}
                    onRecordOutcome={async (runId, input) => {
                      await client.recordWhatIfOutcome(runId, {
                        occurred: input.happened,
                        ...(input.on === undefined ? {} : { occurredOn: input.on }),
                        notes: input.notes,
                      });
                    }}
                  />
                ) : (
                  <WorkspaceView
                    data={workspace}
                    role={user.activeMembership.role}
                    busy={action !== undefined}
                    action={action}
                    runProgress={runProgress}
                    failedScenarioCount={failedScenarioCount}
                    onNavigate={(view) => navigate(view, workspace.book.id)}
                    onAddHoldings={() => navigate("add_holdings", workspace.book.id)}
                    onImportHoldings={() => navigate("imports", workspace.book.id)}
                    onLaunchRun={launchRun}
                    onMonitoringChange={(state) =>
                      performAction("monitoring", () =>
                        client.setMonitoringState(workspace.book.id, state),
                      )
                    }
                    onCreateScenario={(input) =>
                      performAction("scenario", () =>
                        client.createScenario(workspace.book.id, input),
                      )
                    }
                    onCreateWhatIf={(shock) =>
                      performAction("whatif", () => client.createWhatIf(workspace.book.id, shock))
                    }
                    onOpenPosition={(positionId) =>
                      navigate("position_detail", workspace.book.id, positionId)
                    }
                    onOpenAssumption={(assumptionId) =>
                      navigate("assumption_detail", workspace.book.id, assumptionId)
                    }
                    onReaffirmAssumption={(assumptionId, revision, reviewBy) =>
                      void performAction("reaffirm-assumption", () =>
                        client.reaffirmAssumption(assumptionId, {
                          expectedRevision: revision,
                          reviewBy,
                          rationale:
                            "Reaffirmed from the portfolio overview after reviewing the current statement and linked evidence.",
                        }),
                      )
                    }
                  />
                )}
              </>
            )}
          </Suspense>
        </div>
      </main>
    </div>
  );
}

interface WorkspaceViewProps {
  data: WorkspaceData;
  role: CurrentUser["activeMembership"]["role"];
  busy: boolean;
  action: string | undefined;
  runProgress: RunProgress | undefined;
  failedScenarioCount: number;
  onNavigate: (view: ActiveView) => void;
  onAddHoldings: () => void;
  onImportHoldings: () => void;
  onLaunchRun: () => void;
  onMonitoringChange: (state: "live" | "paused") => void;
  onCreateScenario: (input: ScenarioDraftInput) => void;
  onCreateWhatIf: (shock: string) => void;
  onOpenPosition: (positionId: string) => void;
  onOpenAssumption: (assumptionId: string) => void;
  onReaffirmAssumption: (assumptionId: string, revision: number, reviewBy: string) => void;
}

export function WorkspaceView({
  data,
  role,
  busy,
  action,
  runProgress,
  failedScenarioCount,
  onNavigate,
  onAddHoldings,
  onImportHoldings,
  onLaunchRun,
  onMonitoringChange,
  onCreateScenario,
  onCreateWhatIf,
  onOpenPosition,
  onOpenAssumption,
  onReaffirmAssumption,
}: WorkspaceViewProps) {
  const { book, scenarios, positions, assumptions, runs, whatIfs, valuation } = data;
  const dayChangeByPosition = new Map(
    (valuation?.positions ?? []).map((entry) => [entry.positionId, entry]),
  );
  const canAnalyze = role === "analyst" || role === "admin";
  const scenarioTerm = "What-if";
  const latestRun = runs.at(0);
  const progressPercent =
    runProgress === undefined || runProgress.total === 0
      ? 0
      : Math.round((runProgress.done / runProgress.total) * 100);
  const valuedPositions = positions.filter((position) => position.marketValuation !== undefined);
  const marketValue = valuedPositions.reduce(
    (total, position) => total + (position.marketValuation?.marketValue ?? 0),
    0,
  );
  const largestPosition = valuedPositions.reduce<(typeof valuedPositions)[number] | undefined>(
    (largest, position) =>
      largest === undefined ||
      (position.marketValuation?.marketValue ?? 0) > (largest.marketValuation?.marketValue ?? 0)
        ? position
        : largest,
    undefined,
  );
  const largestPositionWeight =
    largestPosition === undefined || marketValue === 0
      ? undefined
      : Math.round(((largestPosition.marketValuation?.marketValue ?? 0) / marketValue) * 100);
  const downsideScenario = scenarios.find(
    (scenario) => scenario.polarity === "negative" && scenario.currentVersion?.pnlEstimate,
  );

  return (
    <ProductPage className="pf-portfolio-page">
      <ProductHeading
        eyebrow={`${book.name} · ${book.monitoringState}`}
        title="Your investments"
        body={
          <p>Holdings, assumptions, evidence, and what-ifs stay connected to the same portfolio.</p>
        }
        actions={
          <div className="header-actions" aria-label="Portfolio actions">
            <button className="button" type="button" onClick={onImportHoldings}>
              Import holdings
            </button>
            <button className="button button-primary" type="button" onClick={onAddHoldings}>
              + Add holdings
            </button>
          </div>
        }
      />

      <section className="pf-metric-grid pf-portfolio-metrics" aria-label="Portfolio overview">
        <ProductMetric
          value={valuedPositions.length === 0 ? "Not priced" : formatMoneyValue(marketValue)}
          label="Market value"
          detail={
            valuedPositions.length === 0
              ? "Connect live prices to calculate"
              : valuation === undefined
                ? `As of ${formatTime(book.updatedAt)}`
                : priceFreshnessNote(valuation)
          }
        />
        <ProductMetric
          value={
            valuation?.change === undefined
              ? "Not available"
              : `${valuation.change.amount < 0 ? "−" : "+"}${formatMoneyValue(Math.abs(valuation.change.amount))}`
          }
          label="Today"
          detail={
            valuation?.change === undefined
              ? "No previous close reported yet"
              : `${valuation.change.percent >= 0 ? "+" : ""}${valuation.change.percent.toFixed(2)}% since the previous close`
          }
          {...(valuation?.change === undefined
            ? {}
            : {
                tone: valuation.change.amount < 0 ? ("negative" as const) : ("positive" as const),
              })}
        />
        <ProductMetric
          value={
            largestPosition === undefined
              ? "—"
              : `${largestPosition.name}${largestPositionWeight === undefined ? "" : ` · ${largestPositionWeight}%`}`
          }
          label="Largest holding"
          detail={
            largestPosition === undefined ? "No valued holdings yet" : "Concentrated exposure"
          }
        />
        <ProductMetric
          value={downsideScenario?.currentVersion?.pnlEstimate ?? "Not assessed"}
          label="Scenario downside"
          tone={downsideScenario === undefined ? "neutral" : "negative"}
          detail={
            downsideScenario?.currentVersion?.name ??
            (latestRun === undefined ? "No analysis run yet" : `Latest run ${latestRun.status}`)
          }
        />
      </section>

      <BookSectionNav activeView="workspace" onNavigate={onNavigate} />

      <div className="pf-portfolio-overview-grid">
        <ProductPanel
          className="exposure-panel"
          title="Holdings"
          meta={`${positions.length} positions${valuedPositions.length > 0 ? " · prices available" : ""}`}
        >
          {positions.length === 0 ? (
            <div className="pf-designed-empty">
              <strong>No holdings yet</strong>
              <p>
                Add what you own to connect assumptions, evidence, and what-ifs to this portfolio.
              </p>
              <button className="button button-primary" type="button" onClick={onAddHoldings}>
                Add holdings
              </button>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Instrument</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Market value</th>
                    <th>Today</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.slice(0, 8).map((position) => (
                    <tr key={position.id}>
                      <td>
                        <button
                          className="table-row-link"
                          type="button"
                          onClick={() => onOpenPosition(position.id)}
                        >
                          {position.name}
                        </button>
                        <span>{position.exposureType}</span>
                      </td>
                      <td className="numeric">
                        {position.marketValuation === undefined
                          ? formatExposureSize(position)
                          : formatQuantity(position.marketValuation.quantity)}
                      </td>
                      <td className="numeric">
                        {position.marketValuation === undefined
                          ? "—"
                          : formatMoneyValue(position.marketValuation.price)}
                      </td>
                      <td className="numeric">
                        {position.marketValuation === undefined
                          ? "Not priced"
                          : formatMoneyValue(position.marketValuation.marketValue)}
                      </td>
                      <td className="numeric">
                        {formatDayChange(dayChangeByPosition.get(position.id)?.dayChange)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ProductPanel>

        <ProductPanel
          className="assumption-panel"
          title="Assumptions being watched"
          meta={`${assumptions.length}`}
        >
          {assumptions.length === 0 ? (
            <div className="pf-designed-empty">
              <strong>No active assumptions</strong>
              <p>
                State what must remain true so Polyfuture can connect it to observable evidence.
              </p>
              <button className="button" type="button" onClick={() => onNavigate("assumptions")}>
                Add an assumption
              </button>
            </div>
          ) : (
            <ul className="pf-assumption-watch-list">
              {assumptions.slice(0, 6).map((assumption) => (
                <li key={assumption.id}>
                  <button type="button" onClick={() => onOpenAssumption(assumption.id)}>
                    <span>
                      <strong>{assumption.text}</strong>
                      <small>
                        {assumption.freshnessState} · affirmed{" "}
                        {formatAffirmationAge(assumption.affirmationAgeDays)} ·{" "}
                        {assumption.tripwireStatus.label} ·{" "}
                        {assumption.links.filter((link) => link.entityType === "scenario").length}{" "}
                        linked what-if
                      </small>
                    </span>
                    <em className={`pf-assumption-state is-${assumption.freshnessState}`}>
                      {assumption.freshnessState}
                    </em>
                  </button>
                  {assumption.freshnessState === "fresh" || !canAnalyze ? null : (
                    <button
                      className="button button-secondary assumption-reaffirm"
                      type="button"
                      disabled={busy || assumption.revision === undefined}
                      onClick={() =>
                        onReaffirmAssumption(
                          assumption.id,
                          assumption.revision!,
                          nextAssumptionReviewAt(assumption.lastAffirmedAt, assumption.reviewBy),
                        )
                      }
                    >
                      Still true
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </ProductPanel>
      </div>

      <ProgressiveDisclosure
        label="Analysis and portfolio controls"
        summary="Monitoring, what-if generation, quick tests, and provenance"
      >
        <div className="pf-advanced-portfolio-grid">
          <ProductPanel
            title="Monitoring and analysis"
            meta={
              <StatusDot
                label={book.monitoringState}
                tone={book.monitoringState === "live" ? "live" : "warning"}
              />
            }
          >
            <button
              className="button"
              type="button"
              disabled={!canAnalyze || busy}
              onClick={() =>
                onMonitoringChange(book.monitoringState === "live" ? "paused" : "live")
              }
            >
              {action === "monitoring"
                ? "Updating…"
                : book.monitoringState === "live"
                  ? "Pause monitoring"
                  : "Start monitoring"}
            </button>
            <button
              className="button button-primary"
              type="button"
              disabled={!canAnalyze || busy || runProgress !== undefined}
              onClick={onLaunchRun}
            >
              {action === "run" ? "Starting…" : "Find portfolio what-ifs"}
            </button>
            {runProgress === undefined ? (
              <p>
                {latestRun === undefined
                  ? "No analysis run yet."
                  : `Latest run ${latestRun.status} · ${formatTime(latestRun.createdAt)}`}
              </p>
            ) : (
              <div className="run-live" aria-live="polite">
                <p>
                  {runProgress.done} of {runProgress.total || "…"} completed
                  {failedScenarioCount > 0 ? ` · ${failedScenarioCount} failed` : ""}
                </p>
                <small>Progress updates automatically.</small>
                <div className="progress-track">
                  <span style={{ width: `${progressPercent}%` }} />
                </div>
              </div>
            )}
          </ProductPanel>
          <ProductPanel
            title={pluralize(scenarioTerm, 2)}
            titleId="scenario-heading"
            meta={`${scenarios.length} tracked`}
          >
            {scenarios.length === 0 ? (
              <p>No governed what-ifs yet.</p>
            ) : (
              <div className="scenario-list">
                {scenarios.slice(0, 3).map((scenario) => (
                  <ScenarioCard key={scenario.id} scenario={scenario} />
                ))}
              </div>
            )}
            {canAnalyze ? (
              <ScenarioComposer
                scenarioTerm={scenarioTerm}
                disabled={busy}
                onSubmit={onCreateScenario}
              />
            ) : (
              <p className="permission-note">Viewer access is read-only.</p>
            )}
          </ProductPanel>
          <ProductPanel title="Quick test" titleId="whatif-heading" meta={`${whatIfs.length} runs`}>
            {canAnalyze ? (
              <WhatIfComposer disabled={busy} onSubmit={onCreateWhatIf} />
            ) : (
              <p className="permission-note">
                Ask an administrator for analyst access to run a test.
              </p>
            )}
          </ProductPanel>
          <ProductPanel
            title="Portfolio provenance"
            meta={`v${book.currentBookVersionNo ?? "draft"}`}
          >
            <p>{book.description || "No description added."}</p>
            <ProvenanceDrawer
              title="Portfolio provenance"
              pins={[
                {
                  label: "Portfolio version",
                  value: book.currentBookVersionId ?? "No published version",
                },
                { label: "Profile", value: `${book.profileKey} ${book.profileVersion}` },
                { label: "Monitoring", value: book.monitoringState },
              ]}
            />
          </ProductPanel>
        </div>
      </ProgressiveDisclosure>
    </ProductPage>
  );
}

/** Share counts are whole or fractional units, never money, so they carry no currency. */
function formatQuantity(value: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(value);
}

function formatMoneyValue(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: value >= 100 ? 0 : 2,
  }).format(value);
}

function formatAffirmationAge(days: number): string {
  if (days === 0) return "today";
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function nextAssumptionReviewAt(
  lastAffirmedAt: string,
  currentReviewBy: string | undefined,
): string {
  const minimumCadence = 86_400_000;
  const defaultCadence = 30 * minimumCadence;
  const priorCadence =
    currentReviewBy === undefined
      ? defaultCadence
      : Math.max(minimumCadence, Date.parse(currentReviewBy) - Date.parse(lastAffirmedAt));
  return new Date(Date.now() + priorCadence).toISOString();
}

function ProductTopbar({
  activeView,
  selectedBook,
  localDemoAccount,
  theme,
  connection,
  menu,
  onToggleTheme,
}: {
  activeView: ActiveView;
  selectedBook: BookRecord | undefined;
  localDemoAccount: boolean;
  theme: Theme;
  connection: ConnectionState;
  /** The menu button, shown only while the rail is a drawer. */
  menu?: {
    ref: RefObject<HTMLButtonElement | null>;
    expanded: boolean;
    onOpen: () => void;
  };
  onToggleTheme: () => void;
}) {
  const contextualBook = isOrganizationWideView(activeView) ? undefined : selectedBook;
  const area = productArea(activeView, contextualBook);
  return (
    <div className="product-topbar">
      {menu === undefined ? null : (
        <button
          ref={menu.ref}
          type="button"
          className="topbar-menu"
          aria-label="Open the menu"
          aria-expanded={menu.expanded}
          aria-controls="app-rail"
          onClick={menu.onOpen}
        >
          <ConceptIcon name="menu" />
        </button>
      )}
      <span className="topbar-title">{area}</span>
      <div className="topbar-actions">
        {localDemoAccount ? <span className="demo-only-badge">Local demo account</span> : null}
        <TopbarConnection state={connection} />
        <button
          type="button"
          className="topbar-theme-toggle"
          aria-label={theme === "dark" ? "Switch to the light theme" : "Switch to the dark theme"}
          onClick={onToggleTheme}
        >
          <span aria-hidden="true">{theme === "dark" ? "☀" : "☾"}</span>
        </button>
      </div>
    </div>
  );
}

function productArea(activeView: ActiveView, selectedBook: BookRecord | undefined): string {
  const book = selectedBook?.name ?? "Portfolio";
  switch (activeView) {
    case "agent":
      return "Ask Polyfuture";
    case "launch":
      return "Set up · What would you like to do first";
    case "today":
      return "Dashboard";
    case "research":
      return "Case study · Second-order transmission · Shadow only";
    case "case_lab":
      return "Case study / leverage and liquidity spiral · Replay lab";
    case "books":
      return "Portfolios";
    case "create_book":
      return "Portfolios / New portfolio";
    case "workspace":
      return "Portfolios";
    case "positions":
    case "position_detail":
      return `${book} / Holdings`;
    case "add_holdings":
      return "My portfolio · Draft / add what you own";
    case "assumptions":
    case "assumption_detail":
      return `${book} / Assumptions`;
    case "scenarios":
      return "What-ifs";
    case "coverage":
      return "What-ifs / What is not covered yet";
    case "scenario_detail":
      return "What-if details";
    case "scenario_diff":
      return "What-ifs / Compare saved versions";
    case "new_scenario":
      return "What-ifs / Explore a change";
    case "evidence":
    case "evidence_new":
    case "evidence_detail":
    case "episode_detail":
      return `${book} / Alerts`;
    case "thesis":
      return `${book} / Find investment assumptions`;
    case "governance":
      return `${book} / Reviews & approvals`;
    case "investigations":
    case "investigation_detail":
      return "Research notes";
    case "decision_packs":
      return "Reports";
    case "report_detail":
      return "Reports / Saved report";
    case "runs":
      return `${book} / Analysis activity`;
    case "run_detail":
      return "What-ifs / Analysis details";
    case "whatif":
      return `${book} / Quick test`;
    case "trading":
      return "Trader";
    case "trading_goal":
      return "Trader / Goal";
    case "calibration":
      return "Calibration";
    case "research_notes_all":
      return "Research notes";
    case "reports_all":
      return "Reports";
    case "analysis_all":
      return "Analysis activity";
    case "reviews_all":
      return "Reviews & approvals";
    case "data":
      return "Market research";
    case "advanced_data":
      return "Advanced data";
    case "saved_results":
      return "Saved results";
    case "connect":
      return `${book} / Add holdings`;
    case "imports":
      return `${book} / Import holdings`;
    case "import_detail":
      return "Holdings / Review import";
    case "admin":
      return "Settings";
    case "account":
      return "Your account · Profile, email addresses & access";
  }
}

function isOrganizationWideView(activeView: ActiveView): boolean {
  return (
    activeView === "scenarios" ||
    activeView === "coverage" ||
    activeView === "scenario_detail" ||
    activeView === "scenario_diff" ||
    activeView === "run_detail" ||
    activeView === "new_scenario" ||
    activeView === "data" ||
    activeView === "advanced_data" ||
    activeView === "saved_results" ||
    activeView === "trading" ||
    activeView === "trading_goal" ||
    activeView === "calibration" ||
    acrossPortfolioKind(activeView) !== undefined
  );
}

/** V-S1d. The pages under More that list every portfolio's items. */
function acrossPortfolioKind(activeView: ActiveView): PortfolioListKind | undefined {
  return activeView === "research_notes_all"
    ? "research_notes"
    : activeView === "reports_all"
      ? "reports"
      : activeView === "analysis_all"
        ? "analysis"
        : activeView === "reviews_all"
          ? "reviews"
          : undefined;
}

/** Where a portfolio's own page for each list lives: the filter opens it. */
const PORTFOLIO_PAGE_FOR: Record<PortfolioListKind, ActiveView> = {
  research_notes: "investigations",
  reports: "decision_packs",
  analysis: "runs",
  reviews: "governance",
  episodes: "evidence",
};

function showsPortfolioSectionNav(activeView: ActiveView): boolean {
  return (
    activeView === "positions" ||
    activeView === "position_detail" ||
    activeView === "assumptions" ||
    activeView === "assumption_detail" ||
    activeView === "evidence" ||
    activeView === "evidence_new" ||
    activeView === "evidence_detail" ||
    activeView === "episode_detail"
  );
}

interface RailItem {
  view: ActiveView;
  label: string;
  icon: ConceptIconName;
  /** Every view that belongs to this item, so a detail page still highlights its section. */
  views: readonly ActiveView[];
  needsPortfolio?: boolean;
}

const primaryRailItems: readonly RailItem[] = [
  { view: "agent", label: "Ask Polyfuture", icon: "sparkles", views: ["agent"] },
  { view: "trading", label: "Trader", icon: "trend", views: ["trading", "trading_goal"] },
  { view: "today", label: "Dashboard", icon: "dashboard", views: ["today", "launch"] },
  {
    view: "books",
    label: "Portfolios",
    icon: "briefcase",
    views: [
      "books",
      "create_book",
      "workspace",
      "positions",
      "position_detail",
      "add_holdings",
      "assumptions",
      "assumption_detail",
      "evidence",
      "evidence_new",
      "evidence_detail",
      "episode_detail",
      "connect",
      "imports",
      "import_detail",
    ],
  },
  {
    view: "scenarios",
    label: "What-ifs",
    icon: "branch",
    views: ["scenarios", "coverage", "scenario_detail", "scenario_diff", "new_scenario", "whatif"],
  },
  { view: "data", label: "Market research", icon: "search", views: ["data"] },
];

const moreRailItems: readonly RailItem[] = [
  {
    view: "research_notes_all",
    label: "Research notes",
    icon: "notebook",
    views: ["research_notes_all", "investigations", "investigation_detail"],
  },
  {
    view: "reports_all",
    label: "Reports",
    icon: "file",
    views: ["reports_all", "decision_packs", "report_detail"],
  },
  {
    view: "analysis_all",
    label: "Analysis activity",
    icon: "activity",
    views: ["analysis_all", "runs", "run_detail"],
  },
  {
    view: "reviews_all",
    label: "Reviews & approvals",
    icon: "shield",
    views: ["reviews_all", "governance", "thesis"],
  },
  { view: "calibration", label: "Calibration", icon: "activity", views: ["calibration"] },
  { view: "research", label: "Case study", icon: "book", views: ["research", "case_lab"] },
  { view: "saved_results", label: "Saved results", icon: "notebook", views: ["saved_results"] },
  { view: "advanced_data", label: "Advanced data", icon: "database", views: ["advanced_data"] },
];

const settingsRailItem: RailItem = {
  view: "admin",
  label: "Settings",
  icon: "settings",
  views: ["admin"],
};

function RailLink({
  item,
  activeView,
  badge,
  disabledReason,
  onSelectView,
}: {
  item: RailItem;
  activeView: ActiveView;
  badge?: string | undefined;
  disabledReason?: string | undefined;
  onSelectView: (view: ActiveView) => void;
}) {
  const selected = item.views.includes(activeView);
  return (
    <button
      type="button"
      className={selected ? "selected" : ""}
      aria-current={selected ? "page" : undefined}
      onClick={() => onSelectView(item.view)}
      disabled={disabledReason !== undefined}
      title={disabledReason ?? item.label}
    >
      <span className="rail-nav-icon">
        <ConceptIcon name={item.icon} />
      </span>
      <span className="rail-nav-label">{item.label}</span>
      {badge === undefined ? null : <small>{badge}</small>}
    </button>
  );
}

function BookRail({
  books,
  selectedBookId,
  user,
  activeView,
  agentRecents,
  attentionCount,
  mode,
  drawerOpen,
  onCloseDrawer,
  onSelectView,
  onNewAgentChat,
  onOpenAgentChat,
  onOpenAccount,
  onSwitchOrganization,
  onLogout,
}: {
  books: BookRecord[];
  selectedBookId: string | undefined;
  user: CurrentUser;
  activeView: ActiveView;
  agentRecents: readonly AgentRecentChat[];
  attentionCount: number | undefined;
  mode: RailMode;
  drawerOpen: boolean;
  onCloseDrawer: () => void;
  onSelectView: (view: ActiveView) => void;
  onNewAgentChat: () => void;
  onOpenAgentChat: (chatId: string) => void;
  onOpenAccount: () => void;
  onSwitchOrganization: (orgId: string) => void;
  onLogout: () => void;
}) {
  const membership = user.activeMembership;
  const suppressCounts = activeView === "launch" || books.length === 0;
  const [moreOpen, setMoreOpen] = useState(false);
  const closeButton = useRef<HTMLButtonElement>(null);
  const asDrawer = mode === "drawer" && drawerOpen;

  useEffect(() => {
    if (!asDrawer) return;
    closeButton.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseDrawer();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [asDrawer, onCloseDrawer]);
  const showMore = moreOpen || moreRailItems.some((item) => item.views.includes(activeView));
  const railBadge = (view: ActiveView): string | undefined => {
    if (suppressCounts) return undefined;
    switch (view) {
      case "today":
        return attentionCount === undefined || attentionCount === 0
          ? undefined
          : String(attentionCount);
      case "books":
        return String(books.length);
      case "scenarios":
        return String(books.reduce((total, book) => total + book.activeScenarioCount, 0));
      default:
        return undefined;
    }
  };
  return (
    <aside
      id="app-rail"
      className="book-rail"
      {...(asDrawer ? { role: "dialog", "aria-modal": true, "aria-label": "Menu" } : {})}
    >
      <div className="brand-lockup">
        <span className="brand-mark" aria-hidden="true">
          P
        </span>
        <div>
          <strong>Polyfuture</strong>
        </div>
        {asDrawer ? (
          <button
            ref={closeButton}
            type="button"
            className="rail-close"
            aria-label="Close the menu"
            onClick={onCloseDrawer}
          >
            <ConceptIcon name="close" />
          </button>
        ) : null}
      </div>
      <button
        className="rail-new-conversation"
        type="button"
        title="New conversation"
        onClick={onNewAgentChat}
      >
        <ConceptIcon name="plus" />
        <span className="rail-nav-label">New conversation</span>
      </button>
      <nav className="view-navigation" aria-label="Workspace areas">
        <p className="rail-label">Workspace</p>
        {primaryRailItems.map((item) => (
          <RailLink
            key={item.view}
            item={item}
            activeView={activeView}
            badge={railBadge(item.view)}
            disabledReason={
              item.needsPortfolio && books.length === 0 ? "Create a portfolio first." : undefined
            }
            onSelectView={onSelectView}
          />
        ))}
        <button
          type="button"
          className="rail-more-toggle"
          aria-label="More"
          title="More"
          aria-expanded={showMore}
          aria-controls="rail-more-items"
          onClick={() => setMoreOpen((open) => !open)}
        >
          <span>More</span>
          <span className={showMore ? "rail-more-chevron open" : "rail-more-chevron"}>
            <ConceptIcon name="chevron" />
          </span>
        </button>
        {showMore ? (
          <div id="rail-more-items" className="rail-more-items">
            {moreRailItems.map((item) => (
              <RailLink
                key={item.view}
                item={item}
                activeView={activeView}
                disabledReason={
                  item.needsPortfolio && selectedBookId === undefined
                    ? "Create a portfolio first."
                    : undefined
                }
                onSelectView={onSelectView}
              />
            ))}
          </div>
        ) : null}
        <hr className="rail-divider" />
        <RailLink
          item={settingsRailItem}
          activeView={activeView}
          disabledReason={membership.role === "admin" ? undefined : "Needs the admin role."}
          onSelectView={onSelectView}
        />
      </nav>
      {/*
        A scrollable region needs its own keyboard access: this rail sets overflow-y, and when no
        recent chats exist it holds nothing focusable, so a keyboard user could not reach or scroll
        it. Firefox reports that as scrollable-region-focusable; Chromium only reports it once the
        content actually overflows, which is why it passed there.
      */}
      <nav aria-label="Recent with Ask Polyfuture" className="agent-recent-navigation" tabIndex={0}>
        <p className="rail-label">Recent with Ask Polyfuture</p>
        {agentRecents.map((chat) => (
          <button
            type="button"
            className="agent-recent-link"
            key={chat.id}
            onClick={() => onOpenAgentChat(chat.id)}
          >
            <span>
              <strong>{chat.title}</strong>
              <small>{formatAgentRecentTime(chat.updatedAt)}</small>
            </span>
          </button>
        ))}
      </nav>
      <div className="rail-account">
        {user.memberships.length > 1 ? (
          <label>
            <span>Organization</span>
            <select
              value={membership.orgId}
              onChange={(event) => onSwitchOrganization(event.target.value)}
            >
              {user.memberships.map((item) => (
                <option value={item.orgId} key={item.orgId}>
                  {item.orgName}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <span className="organization-name">{membership.orgName ?? "Current organization"}</span>
        )}
        <div className="account-row">
          <button className="account-identity" type="button" onClick={onOpenAccount}>
            <span className="account-avatar" aria-hidden="true">
              {(membership.email ?? user.subject).slice(0, 1).toUpperCase()}
            </span>
            <span>
              <strong>{membership.email ?? user.subject}</strong>
              <small>{membership.role}</small>
            </span>
          </button>
          <button type="button" onClick={onLogout}>
            Sign out
          </button>
        </div>
      </div>
    </aside>
  );
}

function ScenarioCard({ scenario }: { scenario: ScenarioRecord }) {
  const version = scenario.currentVersion;
  return (
    <article className={`scenario-card polarity-${scenario.polarity}`}>
      <div className="scenario-meta">
        <span className="polarity-label">{scenario.polarity}</span>
        <StatusPill status={scenario.status} />
        <span className="origin-label">{scenario.origin}</span>
      </div>
      <h3>{version?.name ?? titleCase(scenario.archetypeKey ?? "Elaboration pending")}</h3>
      <p>{version?.thesis ?? "The candidate is queued for validated elaboration."}</p>
      <div className="scenario-footer">
        <span>
          Probability{" "}
          <UncertainProbability
            percent={scenario.probabilityPctUncalibrated}
            horizonDays={scenario.probabilityHorizonDays}
            sourceType={scenario.probabilitySourceType}
            asOf={scenario.probabilityAsOf}
          />
        </span>
        <span>
          P&amp;L <strong>{version?.pnlEstimate ?? "Not estimated"}</strong>
        </span>
      </div>
    </article>
  );
}

function ScenarioComposer({
  scenarioTerm,
  disabled,
  onSubmit,
}: {
  scenarioTerm: string;
  disabled: boolean;
  onSubmit: WorkspaceViewProps["onCreateScenario"];
}) {
  const [name, setName] = useState("");
  const [thesis, setThesis] = useState("");
  const [falsification, setFalsification] = useState("");
  const [polarity, setPolarity] = useState<ScenarioPolarity>("negative");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim().length === 0 || thesis.trim().length === 0 || falsification.trim().length === 0)
      return;
    onSubmit({
      name: name.trim(),
      thesis: thesis.trim(),
      polarity,
      falsification: falsification.trim(),
    });
    setName("");
    setThesis("");
    setFalsification("");
  };
  return (
    <details className="composer">
      <summary>Explore a {scenarioTerm.toLowerCase()}</summary>
      <form onSubmit={submit}>
        <label>
          <span>Name</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={120}
            required
          />
        </label>
        <label>
          <span>What could change?</span>
          <textarea
            value={thesis}
            onChange={(event) => setThesis(event.target.value)}
            rows={3}
            maxLength={200}
            required
          />
        </label>
        <label>
          <span>Expected effect</span>
          <select
            value={polarity}
            onChange={(event) => setPolarity(event.target.value as ScenarioPolarity)}
          >
            <option value="negative">Could hurt my portfolio</option>
            <option value="positive">Could help my portfolio</option>
            <option value="mixed">Could do both</option>
          </select>
        </label>
        <label>
          <span>What would show that this did not happen?</span>
          <textarea
            value={falsification}
            onChange={(event) => setFalsification(event.target.value.replace(/[\r\n]+/g, " "))}
            rows={2}
            maxLength={500}
            required
          />
        </label>
        <button className="button button-primary" type="submit" disabled={disabled}>
          Create and run what-if
        </button>
      </form>
    </details>
  );
}

function WhatIfComposer({
  disabled,
  onSubmit,
}: {
  disabled: boolean;
  onSubmit: (shock: string) => void;
}) {
  const [shock, setShock] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (shock.trim().length === 0) return;
    onSubmit(shock.trim());
    setShock("");
  };
  return (
    <form className="whatif-form" onSubmit={submit}>
      <label htmlFor="whatif-shock">What would you like to test?</label>
      <div>
        <input
          id="whatif-shock"
          value={shock}
          onChange={(event) => setShock(event.target.value)}
          placeholder="What if interest rates rise from 4% to 5%?"
          maxLength={400}
        />
        <button className="button button-primary" type="submit" disabled={disabled}>
          Run test
        </button>
      </div>
    </form>
  );
}

function StatusPill({ status }: { status: string }) {
  return <span className={`status-pill status-${status}`}>{status.replaceAll("_", " ")}</span>;
}

function upsertScenario(scenarios: ScenarioRecord[], incoming: ScenarioRecord): ScenarioRecord[] {
  const index = scenarios.findIndex((scenario) => scenario.id === incoming.id);
  if (index < 0) return [incoming, ...scenarios];
  return scenarios.map((scenario) => (scenario.id === incoming.id ? incoming : scenario));
}

function pluralize(value: string, count: number): string {
  if (count === 1) return value;
  if (value.endsWith("y")) return `${value.slice(0, -1)}ies`;
  return value.endsWith("s") ? value : `${value}s`;
}

function titleCase(value: string): string {
  return value.replaceAll("_", " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatAgentRecentTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Saved chat";
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const day = sameDay
    ? "Today"
    : date.toDateString() === yesterday.toDateString()
      ? "Yesterday"
      : new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
  return `${day} · ${new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date)}`;
}

function newAgentChatId(): string {
  return `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function readSessionJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const value = window.sessionStorage.getItem(key);
    return value === null ? fallback : (JSON.parse(value) as T);
  } catch {
    return fallback;
  }
}

function writeSessionJson(key: string, value: unknown): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Draft handoff persistence is optional in privacy-restricted contexts.
  }
}

function removeSessionItem(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

function holdingValuesForProfile(
  profileKey: string,
  holding: DraftHolding,
): Record<string, unknown> {
  const metadata = {
    currency: holding.currency,
    ...(holding.symbol === undefined ? {} : { ticker_symbol: holding.symbol }),
    ...(holding.quantity === undefined ? {} : { quantity: holding.quantity }),
    ...(holding.costBasis === undefined ? {} : { cost_basis: holding.costBasis }),
  };
  if (profileKey === "individual_investor") {
    return {
      holding_name: holding.name,
      ...(holding.notional === undefined ? {} : { holding_value: holding.notional }),
      ...metadata,
    };
  }
  if (holding.notional === undefined) {
    throw new ApiRequestError(
      "This profile requires a user-stated portfolio value for every holding.",
      422,
      "validation_failed",
    );
  }
  if (profileKey === "asset_manager_risk") {
    return {
      instrument_name: holding.name,
      side: holding.side,
      notional: holding.notional,
      ...metadata,
    };
  }
  if (profileKey === "insurer_cro") {
    return {
      exposure_name: holding.name,
      exposure_type: "listed_security_or_fund",
      market_value: holding.notional,
      ...metadata,
    };
  }
  throw new ApiRequestError(
    "This profile needs fields that the compact holdings form cannot safely infer. Use structured intake.",
    422,
    "validation_failed",
  );
}

function messageFor(error: unknown): string {
  if (error instanceof ApiRequestError) {
    return error.traceId === undefined
      ? error.message
      : `${error.message} · trace ${error.traceId}`;
  }
  return error instanceof Error ? error.message : "An unexpected error interrupted the workspace.";
}

/**
 * Suggests an organization name from the verified identity so the first field is rarely empty.
 * Consumer mailbox domains are skipped: "Gmail" is never the name of anyone's risk organization,
 * and a wrong suggestion is worse than an empty field the operator has to fill in deliberately.
 */
const CONSUMER_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "yahoo.com",
  "icloud.com",
  "me.com",
  "proton.me",
  "protonmail.com",
  "aol.com",
  "gmx.com",
  "mail.com",
  "yandex.com",
  "zoho.com",
]);

function suggestedOrganizationName(credential: IdentityCredential | undefined): string {
  const domain = credential?.email?.split("@")[1]?.toLowerCase();
  if (domain === undefined || CONSUMER_EMAIL_DOMAINS.has(domain)) return "";
  const label = domain.split(".")[0] ?? "";
  return label.length === 0 ? "" : label.charAt(0).toUpperCase() + label.slice(1);
}

/** The same freshness counts the Dashboard shows (V-D2), for the Portfolio page's value card. */
export function priceFreshnessNote(valuation: {
  readonly positionCount: number;
  readonly freshness: { readonly fresh: number; readonly stale: number; readonly unpriced: number };
}): string {
  const { fresh, stale, unpriced } = valuation.freshness;
  return [
    `${fresh} of ${valuation.positionCount} prices current`,
    ...(stale === 0 ? [] : [`${stale} stale`]),
    ...(unpriced === 0 ? [] : [`${unpriced} not priced`]),
  ].join(" · ");
}

function formatDayChange(value: number | undefined): string {
  if (value === undefined) return "—";
  return `${value < 0 ? "−" : "+"}${formatMoneyValue(Math.abs(value))}`;
}
